import type { ReactNode, SVGProps } from "react";

/** Minimal hand-rolled icon set (no icon library dependency). 1.6px stroke, 24px box. */
type IconProps = SVGProps<SVGSVGElement>;

function Svg({ children, ...rest }: IconProps & { children: ReactNode }) {
  return (
    <svg
      viewBox="0 0 24 24"
      width="16"
      height="16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      {...rest}
    >
      {children}
    </svg>
  );
}

export const IconPlus = (p: IconProps) => (
  <Svg {...p}>
    <path d="M12 5v14M5 12h14" />
  </Svg>
);

/** A circled "i" — "what am I looking at?". Deliberately not the other candidate, a
 *  question mark: a question mark promises help with a problem, and this opens an
 *  explanation of the thing already on screen. */
export const IconInfo = (p: IconProps) => (
  <Svg {...p}>
    <circle cx="12" cy="12" r="9" />
    <path d="M12 11v5" />
    <path d="M12 7.75h.01" />
  </Svg>
);

/** A pencil — "edit this". The plain, universal edit affordance; sliders read as
 *  "settings" and a monitor reads as "put it on a screen", which is the card body's job. */
export const IconPencil = (p: IconProps) => (
  <Svg {...p}>
    <path d="M12 20h9" />
    <path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z" />
  </Svg>
);

/** A tray with an arrow rising out of it — "put something of yours in". The vault's
 *  door in the menu; deliberately not a cloud, which reads as "somewhere else". */
export const IconUpload = (p: IconProps) => (
  <Svg {...p}>
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
    <path d="M17 8l-5-5-5 5" />
    <path d="M12 3v12" />
  </Svg>
);

/** Sliders — "screen settings" (device kind + aspect). */
export const IconSliders = (p: IconProps) => (
  <Svg {...p}>
    <path d="M4 8h10M18 8h2M4 16h2M10 16h10" />
    <circle cx="16" cy="8" r="2" />
    <circle cx="8" cy="16" r="2" />
  </Svg>
);

/** A 2×2 grid — "pick a different layout". */
export const IconLayout = (p: IconProps) => (
  <Svg {...p}>
    <rect x="3" y="3" width="7.5" height="7.5" rx="1.2" />
    <rect x="13.5" y="3" width="7.5" height="7.5" rx="1.2" />
    <rect x="3" y="13.5" width="7.5" height="7.5" rx="1.2" />
    <rect x="13.5" y="13.5" width="7.5" height="7.5" rx="1.2" />
  </Svg>
);

/** A house — "Home", the front door / now-playing wall. */
export const IconHome = (p: IconProps) => (
  <Svg {...p}>
    <path d="M4 10.5 12 4l8 6.5" />
    <path d="M6.5 9.5V19a1 1 0 0 0 1 1h3.5v-5h2v5H16.5a1 1 0 0 0 1-1V9.5" />
  </Svg>
);

export const IconTrash = (p: IconProps) => (
  <Svg {...p}>
    <path d="M4 7h16M9 7V5a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2M6 7l1 13a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1l1-13M10 11v6M14 11v6" />
  </Svg>
);

/** A five-point star — a collection's thumbnail pick. Pass `fill="currentColor"` for the
 *  chosen one. */
export const IconStar = (p: IconProps) => (
  <Svg {...p}>
    <path d="M12 3.5l2.6 5.3 5.9.86-4.25 4.14 1 5.85L12 16.9l-5.25 2.75 1-5.85L3.5 9.66l5.9-.86z" />
  </Svg>
);

export const IconCopy = (p: IconProps) => (
  <Svg {...p}>
    <rect x="9" y="9" width="11" height="11" rx="2" />
    <path d="M5 15V5a2 2 0 0 1 2-2h10" />
  </Svg>
);

/** A tile with an arrow rising past it — raise in the paint order. */
export const IconToFront = (p: IconProps) => (
  <Svg {...p}>
    <rect x="4" y="11" width="9" height="9" rx="2" />
    <path d="M16 13V4M12.5 7.5 16 4l3.5 3.5" />
  </Svg>
);

/** The same tile with the arrow diving behind it — lower in the paint order. */
export const IconToBack = (p: IconProps) => (
  <Svg {...p}>
    <rect x="4" y="4" width="9" height="9" rx="2" />
    <path d="M16 11v9M12.5 16.5 16 20l3.5-3.5" />
  </Svg>
);

export const IconCheck = (p: IconProps) => (
  <Svg {...p}>
    <path d="M5 13l4 4L19 7" />
  </Svg>
);

export const IconReload = (p: IconProps) => (
  <Svg {...p}>
    <path d="M3 12a9 9 0 1 0 3-6.7L3 8M3 4v4h4" />
  </Svg>
);

export const IconPlay = (p: IconProps) => (
  <Svg {...p}>
    <path d="M7 5l12 7-12 7V5z" />
  </Svg>
);

/** A clock wearing a record dot (Tabler's clock-record, MIT) — the goon-session START:
 *  what begins is the TIMER. The dot is FILLED, unlike every stroked icon here, because
 *  the whole visual language of "recording" is a solid disc and a 1.6px ring reads as a
 *  radio button instead — the rule the plain IconRecord this replaced established. */
export const IconClockRecord = (p: IconProps) => (
  <Svg {...p}>
    <path d="M21 12.3a9 9 0 1 0 -8.683 8.694" />
    <path d="M12 7v5l2 2" />
    <circle cx="19" cy="19" r="3" fill="currentColor" stroke="none" />
  </Svg>
);

/** A plain clock — the wall tile's Photo time chip: how long a photo holds. */
export const IconClock = (p: IconProps) => (
  <Svg {...p}>
    <circle cx="12" cy="12" r="9" />
    <path d="M12 7v5l3 2" />
  </Svg>
);

/** The universal STOP — a filled square, red at the call site. The goon button wears
 *  this while a session runs, so one control reads as two states of one act. */
export const IconStopSquare = (p: IconProps) => (
  <Svg {...p}>
    <rect x="6" y="6" width="12" height="12" rx="2" fill="currentColor" stroke="none" />
  </Svg>
);

export const IconNext = (p: IconProps) => (
  <Svg {...p}>
    <path d="M6 5l9 7-9 7V5zM18 5v14" />
  </Svg>
);

export const IconBack = (p: IconProps) => (
  <Svg {...p}>
    <path d="M15 6l-6 6 6 6" />
  </Svg>
);

/** Disclosure chevron. Points down for "open"; rotate it -90° for "closed". */
export const IconChevron = (p: IconProps) => (
  <Svg {...p}>
    <path d="M6 9l6 6 6-6" />
  </Svg>
);

export const IconMenu = (p: IconProps) => (
  <Svg {...p}>
    <path d="M4 6h16M4 12h16M4 18h16" />
  </Svg>
);

export const IconMonitor = (p: IconProps) => (
  <Svg {...p}>
    <rect x="3" y="4" width="18" height="12" rx="2" />
    <path d="M8 20h8M12 16v4" />
  </Svg>
);

export const IconTablet = (p: IconProps) => (
  <Svg {...p}>
    <rect x="5" y="3" width="14" height="18" rx="2" />
    <path d="M11 18h2" />
  </Svg>
);

export const IconLink = (p: IconProps) => (
  <Svg {...p}>
    <path d="M10 13a5 5 0 0 0 7 0l2-2a5 5 0 0 0-7-7l-1 1" />
    <path d="M14 11a5 5 0 0 0-7 0l-2 2a5 5 0 0 0 7 7l1-1" />
  </Svg>
);

/** IconLink with a slash — "not linked". The same link shape underneath, so the two
 *  states read as one control changing state rather than two different buttons. */
export const IconLinkOff = (p: IconProps) => (
  <Svg {...p}>
    <path d="M10 13a5 5 0 0 0 7 0l2-2a5 5 0 0 0-7-7l-1 1" />
    <path d="M14 11a5 5 0 0 0-7 0l-2 2a5 5 0 0 0 7 7l1-1" />
    <path d="M5 4l15 16" />
  </Svg>
);

export const IconWarning = (p: IconProps) => (
  <Svg {...p}>
    <path d="M12 3l9 16H3l9-16zM12 10v4M12 17h.01" />
  </Svg>
);

/** Four corners pushing out — enter fullscreen. */
export const IconExpand = (p: IconProps) => (
  <Svg {...p}>
    <path d="M8 3H3v5M16 3h5v5M8 21H3v-5M16 21h5v-5" />
  </Svg>
);

/** Four corners pulling in — leave fullscreen. */
export const IconCollapse = (p: IconProps) => (
  <Svg {...p}>
    <path d="M3 8h5V3M21 8h-5V3M3 16h5v5M21 16h-5v5" />
  </Svg>
);

export const IconClose = (p: IconProps) => (
  <Svg {...p}>
    <path d="M6 6l12 12M18 6L6 18" />
  </Svg>
);

export const IconVolume = (p: IconProps) => (
  <Svg {...p}>
    <path d="M4 9v6h4l5 4V5L8 9H4z" />
    <path d="M16 9.5a3 3 0 0 1 0 5M18.5 7a6 6 0 0 1 0 10" />
  </Svg>
);

export const IconMute = (p: IconProps) => (
  <Svg {...p}>
    <path d="M4 9v6h4l5 4V5L8 9H4z" />
    <path d="M16 10l4 4M20 10l-4 4" />
  </Svg>
);

/**
 * The speaker wearing a character: "1" for one-panel-at-a-time, "R" for radio-only.
 *
 * SET AS TEXT, not drawn as a path. A 1.6px-stroked "R" inside a 24px box is a blob at
 * the 15px this renders at — the first attempt was drawn and read as a smudge — and a
 * filled glyph from the UI font stays legible all the way down. The speaker body is
 * narrowed rather than the character shrunk, so the letter keeps its size.
 *
 * Same speaker as IconVolume underneath, so the four sound modes read as one control
 * changing state rather than four unrelated glyphs (the IconLink/IconLinkOff rule).
 */
const VolumeWith = ({ ch, ...p }: IconProps & { ch: string }) => (
  <Svg {...p}>
    <path d="M3 9v6h3.5l4.5 4V5L6.5 9H3z" />
    <text
      x="14"
      y="17"
      fontSize="13"
      fontWeight="700"
      fill="currentColor"
      stroke="none"
      fontFamily="ui-sans-serif, system-ui, sans-serif"
    >
      {ch}
    </text>
  </Svg>
);

/** One panel of the wall carries the sound. */
export const IconVolumeOne = (p: IconProps) => <VolumeWith ch="1" {...p} />;

/** The radio channel plays and every panel is silent. */
export const IconVolumeRadio = (p: IconProps) => <VolumeWith ch="R" {...p} />;

/** Pause, for the radio channel's transport. The one transport glyph the set was missing —
 *  IconPlay, IconNext and IconBack were already here. */
export const IconPause = (p: IconProps) => (
  <Svg {...p}>
    <path d="M9 5v14M15 5v14" />
  </Svg>
);

/** IconVolume with a line through it — "this region is silent". The same speaker
 *  underneath so the two states read as one control (the IconLink/IconLinkOff rule);
 *  distinct from IconMute's X, which is the SCREEN's master mute in the bar. */
export const IconVolumeOff = (p: IconProps) => (
  <Svg {...p}>
    <path d="M4 9v6h4l5 4V5L8 9H4z" />
    <path d="M20 4L5 19" />
  </Svg>
);

/** Two wedges across a dashed axis — mirrored playback for a synced pair. */
export const IconMirror = (p: IconProps) => (
  <Svg {...p}>
    <path d="M12 3v18" strokeDasharray="2 2.5" />
    <path d="M8 7L3 12l5 5V7z" />
    <path d="M16 7l5 5-5 5V7z" />
  </Svg>
);

/** Two chasing arrows — "play this clip forever" (the region's lock/repeat). */
export const IconLoop = (p: IconProps) => (
  <Svg {...p}>
    <path d="M17 3l3 3-3 3" />
    <path d="M20 6H9a5 5 0 0 0-5 5v1" />
    <path d="M7 21l-3-3 3-3" />
    <path d="M4 18h11a5 5 0 0 0 5-5v-1" />
  </Svg>
);

/** Four-point sparkle — "this was made for you": a keyword the vision model assigned
 *  (TagBubble), the editor's ready-made quick setups. Filled rather than stroked so it
 *  still reads at the 10px the tag badge renders it at. */
export const IconSparkle = (p: IconProps) => (
  <Svg {...p}>
    <path
      d="M12 3l1.9 5.1L19 10l-5.1 1.9L12 17l-1.9-5.1L5 10l5.1-1.9L12 3z"
      fill="currentColor"
      stroke="none"
    />
  </Svg>
);

/** Flag — "report this as wrong". */
export const IconFlag = (p: IconProps) => (
  <Svg {...p}>
    <path d="M5 21V4M5 4h11l-2 3.5L16 11H5" />
  </Svg>
);

/** An open eye — "shown". The screen-code chip wears it while the code card is up. */
export const IconEye = (p: IconProps) => (
  <Svg {...p}>
    <path d="M2.5 12s3.5-6 9.5-6 9.5 6 9.5 6-3.5 6-9.5 6-9.5-6-9.5-6z" />
    <circle cx="12" cy="12" r="3" />
  </Svg>
);

/** The same eye, struck through — "hidden". One glyph, one meaning, no tint to decode. */
export const IconEyeOff = (p: IconProps) => (
  <Svg {...p}>
    <path d="M2.5 12s3.5-6 9.5-6 9.5 6 9.5 6-3.5 6-9.5 6-9.5-6-9.5-6z" />
    <circle cx="12" cy="12" r="3" />
    <path d="M4 20 20 4" />
  </Svg>
);

/*
 * THE ☰ MENU'S COLUMN. The seven below exist for one job: the leading glyph on a site-menu
 * row (SiteBar's MenuRowIcon). A menu row is read at a glance and half the time not read
 * at all — the shape is what the eye lands on — so each of these has to be recognisable at
 * 16px with nothing else on the row to disambiguate it. Views and Screen already had
 * theirs (IconLayout, IconMonitor) and reuse them rather than growing near-duplicates.
 */

/** A magnifier — "go looking". Browse is the site's one search-and-scroll surface. */
export const IconSearch = (p: IconProps) => (
  <Svg {...p}>
    <circle cx="11" cy="11" r="7" />
    <path d="m16.5 16.5 4 4" />
  </Svg>
);

/** Two stacked plates — a COLLECTION: more than one thing, kept together. Deliberately
 *  not a folder, which reads as a file manager, and not a bookmark, which is one item. */
export const IconStack = (p: IconProps) => (
  <Svg {...p}>
    <path d="M12 3 3 7.5 12 12l9-4.5L12 3Z" />
    <path d="m3 13 9 4.5 9-4.5" />
  </Svg>
);

/**
 * A phone THROWING SIGNAL — the Remote, and the app's own words for it: "control from
 * your phone". Four handset-shaped candidates were tried first (a body with a top button,
 * with a d-pad, with the streaming-remote dot grid) and every one of them collapsed into
 * an anonymous rounded pill at 16px, where a menu row actually reads it. The waves are
 * what save it: they break the silhouette, so this is the one glyph in the column that
 * cannot be mistaken for a rectangle. They also read against IconMonitor one row above —
 * a thing you watch, and the thing that drives it.
 */
export const IconRemote = (p: IconProps) => (
  <Svg {...p}>
    <rect x="6" y="3" width="9" height="18" rx="2.5" />
    <path d="M18 8.5a5 5 0 0 1 0 7" />
    <path d="M20.5 6a8.5 8.5 0 0 1 0 12" />
  </Svg>
);

/** An OPEN book — the guide. Help is a book everywhere, and the alternative (a question
 *  mark) is already spoken for by IconInfo's "what am I looking at?". Open rather than
 *  closed, because a closed book at 16px is a rectangle with a line in it and half this
 *  set is already rectangles; the two pages meeting at a spine are what make it a book. */
export const IconBag = (p: IconProps) => (
  <Svg {...p}>
    <path d="M5.5 8h13l1 12.5h-15Z" />
    <path d="M9 10.5V7a3 3 0 0 1 6 0v3.5" />
  </Svg>
);

export const IconBook = (p: IconProps) => (
  <Svg {...p}>
    <path d="M3 5.5h6a3 3 0 0 1 3 3v11a2.5 2.5 0 0 0-2.5-2.5H3Z" />
    <path d="M21 5.5h-6a3 3 0 0 0-3 3v11a2.5 2.5 0 0 1 2.5-2.5H21Z" />
  </Svg>
);

/**
 * Two arrows trading places — SWITCH the thing that is playing for another one.
 *
 * Not IconLayout, which the Views row three lines above already wears: two identical grids
 * in one open menu is a reader checking which is which. And VERTICAL, not the horizontal
 * exchange this started as — Screen settings sits directly underneath wearing IconSliders,
 * and two glyphs made of horizontal strokes were the same collision one row further down.
 */
export const IconSwap = (p: IconProps) => (
  <Svg {...p}>
    <path d="M9 20V5m-2.5 2.5L9 5l2.5 2.5" />
    <path d="M15 4v15m2.5-2.5L15 19l-2.5-2.5" />
  </Svg>
);

/** Head and shoulders — your profile. */
export const IconUser = (p: IconProps) => (
  <Svg {...p}>
    <circle cx="12" cy="8" r="3.5" />
    <path d="M5.5 20a6.5 6.5 0 0 1 13 0" />
  </Svg>
);

/** An arrow going INTO the door — sign in. The pair below is the only place in this set
 *  where direction carries the whole meaning, so the door stays on the same side in both
 *  and only the arrow turns around. */
export const IconSignIn = (p: IconProps) => (
  <Svg {...p}>
    <path d="M14 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4" />
    <path d="m10 8 4 4-4 4M14 12H4" />
  </Svg>
);

/** The same door, the arrow going OUT — sign out. */
export const IconSignOut = (p: IconProps) => (
  <Svg {...p}>
    <path d="M14 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4" />
    <path d="m8 8-4 4 4 4M4 12h10" />
  </Svg>
);
